import { useCallback, useMemo, useRef } from 'react'

import { useComposedRefs } from '@radix-ui/react-compose-refs'

import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import {
    GalleryWidgetImageWrapperStyles,
    GalleryWidgetLayoutStyles,
} from 'features/views/LayoutEditor/widgets/GalleryWidget/GalleryWidget.css'
import { GalleryWidgetType } from 'features/views/LayoutEditor/widgets/GalleryWidget/types'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { RecipeProps } from 'ui/helpers/recipes'
import { useContainerResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

type ImagesVisibleCount = NonNullable<
    RecipeProps<typeof GalleryWidgetLayoutStyles>['visibleImageCount']
>

const MAX_IMAGES_VISIBLE_SINGLE: ImagesVisibleCount = 1
const MAX_IMAGES_VISIBLE_MULTI: ImagesVisibleCount = 5

export type GalleryWidgetImageValue = {
    src: string
    alt: string
    attributes: Record<string, unknown>
    gridArea: string
    exceedsLimit?: boolean
}

export function useGalleryWidgetState(widget: GalleryWidgetType) {
    const { fields } = useLayoutEditorContext()
    const { record, isLoadingSlow } = useRecordManagerContext()

    const { aspectRatio, style, allowFullImagePreview } = widget.attrs
    const maxImageCount: ImagesVisibleCount =
        style === 'single' ? MAX_IMAGES_VISIBLE_SINGLE : MAX_IMAGES_VISIBLE_MULTI

    const fieldsByApiName = fields.reduce((acc, field) => {
        return acc.set(field.api_name, field)
    }, new Map<string, FieldDto>())

    let images = widget.attrs.images.reduce((acc, image) => {
        if (image.type === 'field') {
            const field = fieldsByApiName.get(image.fieldApiName)
            if (field) {
                const value = getFieldValue(record, field)
                const valuesForField = value.map((src, idx) => {
                    const index = acc.length + idx
                    const attributes = getFileAttributes(src, index)
                    const exceedsLimit = index >= maxImageCount

                    return {
                        src,
                        alt: field.label,
                        attributes,
                        gridArea: getGridArea(index),
                        exceedsLimit,
                    }
                })
                acc.push(...valuesForField)
            }
        } else if (image.type === 'file') {
            const index = acc.length
            const attributes = getFileAttributes(image.url, index)
            const exceedsLimit = index >= maxImageCount

            acc.push({
                src: image.url,
                alt: image.filename,
                attributes,
                gridArea: getGridArea(index),
                exceedsLimit,
            })
        }

        return acc
    }, [] as GalleryWidgetImageValue[])
    if (style === 'single' && images.length > 1) {
        images = images.slice(0, 1)
    }

    const imagesMemo = useDeepEqualsMemoValue(images)

    // The aspect ratio is only used for single images.
    const effectiveAspectRatio = style === 'single' ? aspectRatio : undefined

    const downloadArchiveFiles = useDeepEqualsMemoValue(imagesMemo.map((image) => image.src))
    const downloadArchiveName = String(record?._primary || 'images')

    const imageCount = imagesMemo.length

    const visibleImageCount: ImagesVisibleCount = Math.min(
        imageCount,
        maxImageCount
    ) as ImagesVisibleCount

    const imageSelector = useMemo(() => {
        return `.${GalleryWidgetImageWrapperStyles.styleFunction({})}`
    }, [])

    const { ref: containerRef, value: isSmall } = useContainerResponsiveValue({
        mobile: true,
        tabletLarge: false,
    })

    const containerLocalRef = useRef<HTMLDivElement>(null)
    const containerComposedRef = useComposedRefs<HTMLDivElement>(containerRef, containerLocalRef)

    const overflowButtonLabel = isSmall ? '' : 'Show all'
    const overflowButtonSize = isSmall ? ('xs' as const) : ('s' as const)

    const showOverflowButton = allowFullImagePreview && imageCount > maxImageCount

    const onOverflowButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()

        const container = containerLocalRef.current
        if (!container) return

        // Find the first overflowing image.
        const overflowSelector = `.${GalleryWidgetImageWrapperStyles.styleFunction({
            exceedsLimit: true,
        })
            .split(' ')
            .join('.')}`
        const firstOverflowImage = container.querySelector(overflowSelector) as HTMLElement | null
        if (!firstOverflowImage) return

        firstOverflowImage.click()
    }, [])

    return {
        isLoading: isLoadingSlow,
        images: imagesMemo,
        aspectRatio: effectiveAspectRatio,
        downloadArchiveFiles,
        downloadArchiveName,
        visibleImageCount,
        showOverflowButton,
        onOverflowButtonClick,
        overflowButtonLabel,
        overflowButtonSize,
        containerRef: containerComposedRef,
        imageSelector,
    }
}

function getFieldValue(record?: RecordDto, field?: FieldDto): string[] {
    if (!record || !field) return []

    const value = record[field.api_name] ?? undefined
    if (!value) return []

    if (field.type === 'multi_file') {
        if (Array.isArray(value)) {
            return value.map((v) => v.url)
        } else if (value && typeof value === 'object') {
            return [(value as any).url]
        }
    }

    return Array.isArray(value) ? value : [value]
}

function getFileAttributes(url: string, idx: number) {
    return {
        'data-index': idx,
        'data-download': true,
        'data-download-url': url,
        'data-src': url,
    }
}

function getGridArea(idx: number) {
    return String.fromCharCode(97 + idx)
}
